<template>
  <modal v-if="showModal" class="selection-modal" @close="hideModalCancel" @hook:mounted="handleModalMounted">
    <div slot="header">
      <span>{{modalTitle}}</span>
      <span class="header-close-icon" @click="hideModalCancel"><i class="fal fa-times-circle"></i></span>
    </div>
    <div slot="body">
      <div class="body-container">
        <div v-if="sources && sources.length > 0" class="source-list-sidebar styleScroll">
          <div class="source-list-header">Sources</div>
          <div class="source-list-item"
               v-for="source in sources"
               :key="source.id"
               @mouseover="handleSourceMouseEnter(source.options)"
               @mouseleave="handleSourceMouseLeave"
          >
            {{ source.name }}
          </div>
        </div>
        <div class="content-container">
          <div class="content-header">
            <div class="select-all-checkbox">
              <label class="checkbox-label">
                <input type="checkbox" @change="selectAllChangeHandler" :checked="isSelectAllChecked">
                <span class="checkbox-label">Select All</span>
              </label>
            </div>

            <div :class="['search-field', { 'no-sidebar': !sources || !sources.length }]">
              <v-text-field
                placeholder="Search by option title or description"
                outlined
                dense
                v-model="optionSearchInput"
                @input="handleOptionSearchChange"
              ></v-text-field>
            </div>

            <div class="descriptions-toggle">
              <label class="legend-title legend-switch" tabindex="0" @keypress.enter="showDescriptions = !showDescriptions">
                <input id="descriptionsToggleSwitch" type="checkbox" checked v-model="showDescriptions">
                <span class="legend-slider"></span>
              </label>
              <label for="descriptionsToggleSwitch" class="legend-title">Show Descriptions</label>
            </div>
          </div>
          <div class="content-body">

            <!-- Option checkboxes. Different styles and markup based on whether categories are specified. -->
            <div v-if="categories && categories.length > 0" class="option-checkbox-container">
              <div class="option-category" v-for="category in categories" :key="category.id">
                <label class="checkbox-label">
                  <input type="checkbox" @change="categoryCheckboxChangeHandler($event, category.options)" :checked="isCategorySelected(category.options)">
                  <span class="checkbox-label category-label">{{ category.name }}</span>
                </label>
                <div class="option-category-list">
                  <div :class="[{ disabled: isOptionDisabled(option.name) }, 'option-checkbox']" v-for="option in getOptionsInCategory(category.id)" :key="option.name">
                    <label class="checkbox-label">
                      <input type="checkbox" :value="option.name" v-model="selectedOptions" @change="optionSelectionChangeHandler">
                      <v-tooltip bottom open-delay="500">
                        <template v-slot:activator="{ on }">
                          <span class="checkbox-label" v-on="on">{{ option.title }}</span>
                        </template>
                        <span>{{ option.title }}</span>
                      </v-tooltip>
                    </label>
                    <div v-show="showDescriptions" class="option-description">{{ option.description || 'no description specified' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!categories || !categories.length" class="option-checkbox-container no-categories">
              <div :class="[{ disabled: isOptionDisabled(option.name) }, 'option-checkbox']" v-for="option in options" :key="option.name">
                <label class="checkbox-label">
                  <input type="checkbox" :value="option.name" v-model="selectedOptions" @change="optionSelectionChangeHandler">
                  <v-tooltip bottom open-delay="500">
                    <template v-slot:activator="{ on }">
                      <span class="checkbox-label" v-on="on">{{ option.title }}</span>
                    </template>
                    <span>{{ option.title }}</span>
                  </v-tooltip>
                </label>
                <div v-show="showDescriptions" class="option-description">{{ option.description || 'no description specified' }}</div>
              </div>
            </div>
            <!-- /Option checkboxes -->

          </div>
          <div class="footer-container">
            <div class="reset-link-container">
              <div class="reset-link" @click="resetSelection" @keyup.enter="resetSelection" @mouseover="showResetTooltip = true" @mouseleave="showResetTooltip = false" tabindex="0">
                <span class="reset-link-icon"><i class="fas fa-undo"></i></span>
                <span class="reset-link-text">Reset to Defaults</span>
              </div>
              <div class="reset-link-tooltip" v-show="showResetTooltip">This will reset all options<br>back to their default state</div>
            </div>
            <div class="button-container">
              <span class="minimum-selection-text" v-show="selectedOptions.length === 0">
                Please select at least 1 option or attribute
              </span>
              <button @click="hideModalCancel" class="cancel-button btn btn-default">Cancel</button>
              <button @click="hideModalOK" class="ok-button btn btn-primary" :disabled="selectedOptions.length === 0">OK</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer">
      <!-- empty slot -- for layout reasons we move the footer content into the "body" slot -->
    </div>
  </modal>
</template>

<script>
import Mark from 'mark.js/dist/mark.min';
import modal from '../../modal/src/modal.vue';

export default {
  name: 'SelectionModal',
  components: {
    modal
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    modalTitle: {
      type: String,
      default: 'Select Attributes'
    },
    sources: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    initialSelectedOptions: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      selectedOptions: [],
      oldSelectedOptions: [],
      showDescriptions: false,
      showResetTooltip: false,
      optionSelectionChanged: false,
      optionSearchInput: '',
      disabledOptions: []
    }
  },
  computed: {
    isSelectAllChecked: function () {
      return this.options.length === this.selectedOptions.length;
    }
  },
  watch: {
    showModal(newVal) {
      if (newVal) { // update selections when the modal is opened
        this.oldSelectedOptions = [...this.initialSelectedOptions];
        this.selectedOptions = [...this.initialSelectedOptions];
      }
    }
  },
  methods: {
    categoryCheckboxChangeHandler: function (event, categoryOptions) {
      let self = this;
      if (event.target.checked) {
        categoryOptions.forEach(opt => {
          if (!self.selectedOptions.includes(opt)) {
            self.selectedOptions.push(opt);
          }
        })
      } else {
        self.selectedOptions = self.selectedOptions.filter(opt => {
          return !categoryOptions.includes(opt);
        });
      }
      self.optionSelectionChanged = true;
      self.emitOptionSelectionChange();
    },
    optionSelectionChangeHandler: function () {
      this.optionSelectionChanged = true;
      this.emitOptionSelectionChange();
    },
    getOptionsInCategory: function (categoryId) {
      let self = this;

      if (!self.categories || !self.categories.length > 0) {
        return [];
      }

      let filteredCols = [];
      let category = self.categories.find(cat => cat.id === categoryId);
      if (!category) {
        throw new Error('category not found: ' + categoryId);
      }
      self.options.forEach(opt => {
        if (category.options.includes(opt.name)) {
          filteredCols.push(opt);
        }
      });
      return filteredCols;
    },
    handleOptionSearchChange: function (val) {
      let self = this;

      if (self.markJS) {
        self.markJS.unmark({
          done: function () {
            self.markJS.mark(val, {
              element: 'span',
              className: 'highlight',
              separateWordSearch: false
            });
          }
        });
      }
    },
    handleModalMounted: function () {
      this.markJS = new Mark(document.querySelector('.content-body .option-checkbox-container'));
    },
    handleSourceMouseEnter: function (sourceOptions) {
      this.disabledOptions = this.options.map(opt => opt.name).filter(opt => !sourceOptions.includes(opt));
    },
    handleSourceMouseLeave: function () {
      this.disabledOptions = [];
    },
    hideModalCancel: function () {
      this.selectedOptions = [...this.oldSelectedOptions];
      this.optionSelectionChanged = false;
      this.$emit('option-selection-cancel');
    },
    hideModalOK: function () {
      if (this.optionSelectionChanged) {
        this.emitOptionSelection()
        this.optionSelectionChanged = false;
      } else {
        this.$emit('option-selection-cancel');
      }
    },
    isCategorySelected: function (categoryOptions) {
      return categoryOptions.every(opt => this.selectedOptions.includes(opt));
    },
    isOptionDisabled: function (optId) {
      return this.disabledOptions.includes(optId);
    },
    resetSelection: function () {
      this.selectedOptions = [...this.oldSelectedOptions];
    },
    selectAllChangeHandler: function (event) {
      if (event.target.checked) {
        this.selectedOptions = this.options.map(opt => {
          return opt.name
        });
      } else {
        this.selectedOptions = [];
      }
      this.optionSelectionChanged = true;
      this.emitOptionSelectionChange();
    },
    emitOptionSelection: function () {
      /**
       * Option selection changed (emitted only when the modal is closed)
       * 
       * @type {Array}
       */
      this.$emit('option-selection', this.selectedOptions);
    },
    emitOptionSelectionChange: function () {
      /**
       * Option selection changed (emitted on each change)
       * 
       * @type {Array}
       */
      this.$emit('option-selection-change', this.selectedOptions);
    },
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
$modal-header-height: 75px;
$content-header-height: 82px;
$content-body-height: 600px;
$content-footer-height: 72px;
$modal-max-height: calc(100vh - 40px);
$modal-body-max-height: calc(#{$modal-max-height} - #{$modal-header-height});
$content-body-max-height: calc(#{$modal-body-max-height} - #{$content-header-height} - #{$content-footer-height});
.selection-modal.modal-mask {
  .modal-wrapper {
    .modal-container {
      .modal-header {
        .header-close-icon {
          float: right;
          font-size: 24px;
          color: #6D787E;
          position: relative;
          bottom: 10px;
          left: 10px;
          cursor: pointer;

          &:hover {
            color: var(--text-color);
          }
        }
      }
      .modal-body {
        margin: 0;
        padding: 0;
        border-bottom: none;
        max-height: $modal-body-max-height;

        .body-container {
          display: flex;

          .checkbox-label input[type="checkbox"] {
            accent-color: var(--default-color);
          }

          .source-list-sidebar {
            flex: 1;
            background: var(--background-color); // todo make this different
            color: var(--text-color);
            max-height: $modal-body-max-height;
            overflow-y: auto;

            .source-list-header {
              font-weight: bold;
              padding: 44px 0 10px 23px;
              font-size: 12px;
            }

            .source-list-item {
              background: var(--table-header-color);
              padding: 10px 0 10px 23px;
              margin-bottom: 2px;
              font-size: 11px;

              &:hover {
                background: var(--table-header-hover-color);
              }
            }
          }

          .content-container {
            flex: 6;
            margin: 0 20px;

            .content-header {
              display: flex;
              justify-content: space-between;
              border-bottom: 1px solid #BFBFBF;
              padding-left: 0;

              .select-all-checkbox {
                margin: 10px 0;
                text-align: left;
                flex: 1;
              }

              .search-field {
                margin-left: 2%;
                flex: 3;
                max-width: 300px;

                &.no-sidebar {
                  margin-left: 16%;
                }

                .v-input .v-input__control .v-input__slot input {
                  font-size: 12px;
                }
                
                .theme--light.v-input input,
                .theme--light.v-input input::placeholder,
                .theme--light.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state) > .v-input__control > .v-input__slot fieldset {
                  color: var(--text-color);
                }
                .v-text-field--outlined fieldset {
                  border-color: var(--text-color);
                }

              }

              .descriptions-toggle {
                text-align: right;
                flex: 2;

                // TODO: most legend-switch styles copy/pasted and modified from vAnalytics
                /*legend-switch*/
                $slider-width: 9px;
                $slider-width-small: 6px;

                .legend-title {
                  font-size: 12px;
                  font-weight: normal;
                }

                .legend-switch input {
                  display: none;
                }

                .legend-switch {
                  display: inline-block;
                  width: $slider-width * 3 + 1;
                  height: $slider-width * 2;
                  margin: 5px;
                  transform: translateY(50%);
                  position: relative;
                  cursor: pointer;
                }

                .legend-switch.legend-switch-small {
                  width: $slider-width-small * 3 + 1;
                  height: $slider-width-small * 2;
                  transform: unset;
                  margin: 5px 2px 0 2px;
                }

                .legend-slider {
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  left: 0;
                  right: 0;
                  border-radius: $slider-width;
                  box-shadow: 0 0 0 1px #00FF00, 0 0 1px #00FF00;
                  cursor: pointer;
                  border: 0 solid transparent;
                  overflow: hidden;
                  transition: .4s;
                }

                .legend-slider.legend-slider-small {
                  border-radius: $slider-width-small;
                }

                .legend-slider:before {
                  position: absolute;
                  content: "";
                  width: 100%;
                  height: 100%;
                  background:var(--panel-background);
                  border-radius: $slider-width + 1;
                  transform: translateX(-($slider-width + 1));
                  transition: .4s;
                  box-shadow:0 0 0 1px var(--disabled-color),0 0 1px var(--disabled-color);
                }

                .legend-slider.legend-slider-small:before {
                  border-radius: $slider-width-small + 1;
                  transform: translateX(-($slider-width-small + 1));
                }

                input:checked+.legend-slider:before {
                  transform: translateX($slider-width + 1);
                  background:var(--panel-background);
                  box-shadow:0 0 0 1px var(--primary-btn-bg-color),0 0 1px var(--primary-btn-bg-color);
                }

                input:checked+.legend-slider.legend-slider-small:before {
                  transform: translateX($slider-width-small + 1);
                background:var(--panel-background);
                box-shadow:0 0 0 1px var(--primary-btn-bg-color),0 0 1px var(--primary-btn-bg-color);
                }

                @supports (-ms-ime-align: auto) {
                  .legend-slider:before {
                    transform: translate(-($slider-width *3 - 3));
                  }

                  .legend-slider.legend-slider-small:before {
                    transform: translate(-($slider-width-small *3 - 3));
                  }

                  input:checked+.legend-slider:before {
                    transform: translateX(-(($slider-width + 1)/2));
                  }

                  input:checked+.legend-slider.legend-slider-small:before {
                    transform: translateX(-(math.div(($slider-width-small + 1), 2)));
                  }
                }

                input:checked+.legend-slider {
                  background:var(--primary-btn-bg-color);
                  box-shadow:0 0 0 2px var(--primary-btn-bg-color),0 0 2px var(--primary-btn-bg-color);
                }

                input+.legend-slider {
                  box-shadow:0 0 0 2px var(--disabled-color),0 0 2px var(--disabled-color);
                  background:var(--disabled-color);
                }
              }
            }

            .content-body {
              height: $content-body-height;
              max-height: $content-body-max-height;
              overflow-y: auto;
              padding-top: 20px;
              border-bottom: 1px solid #BFBFBF;

              .option-checkbox-container {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
                column-gap: 30px;

                &.no-categories {
                  grid-template-columns: repeat(4, minmax(0, 1fr));
                }

                .option-checkbox {
                  margin: 0 0 0 20px;
                  overflow: hidden;

                  label {
                    cursor: pointer;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                  }

                  .option-description {
                    font-size: 10px;
                    line-height: 12px;
                    color: var(--text-color);
                    position: relative;
                    top: -12px;
                    margin-bottom: 5px;
                  }

                  &.disabled {
                    opacity: 0.15;
                  }
                }

              }
            }
            
            .checkbox-label {
              font-size: 12px;
              font-weight: normal;
              color: var(--text-color);
              margin-bottom: 0;
              position: relative;
              bottom: 2px;

              &.category-label {
                font-weight: bold;
              }
            }
          }
        }

        .footer-container {
          height: $content-footer-height;
          padding: 20px 0;
          border: none;
          display: flex;
          align-items: center;

          .reset-link-container {
            flex: 1;
            height: 100%;

            .reset-link {
              color: var(--hover-color);
              cursor: pointer;
              font-size: 12px;
              font-weight: 600;
              display: inline-block;
              height: 100%;
              vertical-align: middle;
            }

            .reset-link-tooltip {
              font-size: 9px;
              line-height: 11px;
              display: inline-block;
              margin-left: 10px;
            }
          }

          .button-container {
            text-align: right;
            flex: 2;

            button {
              margin: 0 5px;
            }

            .minimum-selection-text {
              font-size: 11px;
              color: #DD4B39;
              margin-right: 20px;
            }
          }
        }
      }

      .modal-footer {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss">
span.highlight {
  padding: 0;
  background-color: var(--primary-background-active);
}
</style>